import React, { useState } from 'react';
import { Button, Drawer, Badge, Dropdown, Tooltip } from 'antd';
import { FilterOutlined, MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import useResponsive from '../../hooks/useResponsive';

/**
 * Responsive page filter bar.
 *
 * Desktop (>640px) — single inline row:
 *   [search] [filter1] [filter2] [spacer] [secondaryBtn] [primary]
 *
 * Mobile (≤640px) — two stacked rows:
 *   Row 1: [search (flex 1)] [Filters icon] [••• secondary menu if any]
 *   Row 2: [primary CTA — full width]
 *
 * Props:
 *   search             ReactNode — the Search input
 *   filters            Array<ReactNode> — Selects, etc. (drawer on mobile)
 *   activeFilterCount  number — badge count next to Filters icon
 *   primaryAction      { label, icon, onClick, type?, style?, danger?, loading? }
 *   secondaryActions   Array<{ key, label, icon, onClick, danger? }>
 */

const DesktopBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
  flex-wrap: wrap;

  .spacer { flex: 1; }
`;

const MobileBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  width: 100%;

  .top-row {
    display: flex;
    align-items: stretch;
    width: 100%;

    /* Connect search input and trailing buttons into a single segmented
       control: no gap, shared borders, only the outer corners rounded. */
    .search {
      flex: 1 1 auto;
      min-width: 0;
      .ant-input-affix-wrapper,
      .ant-input-search,
      .ant-input {
        width: 100% !important;
        height: 40px;
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    }
    .ant-btn {
      height: 40px;
      flex-shrink: 0;
      border-radius: 0;
      margin-left: -1px; /* collapse double border */
    }
    .ant-btn:last-child {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
    .ant-btn-icon-only {
      width: 40px;
      padding: 0;
    }
    .ant-badge {
      line-height: 0;
      display: inline-flex;
    }
    /* When wrapped in a Badge the button still needs to share its border */
    .ant-badge .ant-btn {
      border-radius: 0;
    }
  }

  .primary-row .ant-btn {
    width: 100%;
    height: 44px;
    font-weight: 600;
  }
`;

const DrawerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-select,
  .ant-input-affix-wrapper {
    width: 100% !important;
  }
`;

const ResponsiveFilterBar = ({
  search,
  filters = [],
  activeFilterCount = 0,
  primaryAction,
  secondaryActions = []
}) => {
  const { isMobile } = useResponsive();
  const [open, setOpen] = useState(false);

  const renderPrimary = (extraStyle = {}) => primaryAction && (
    <Button
      type={primaryAction.type || 'primary'}
      icon={primaryAction.icon}
      danger={primaryAction.danger}
      loading={primaryAction.loading}
      onClick={primaryAction.onClick}
      style={{ height: 40, ...primaryAction.style, ...extraStyle }}
    >
      {primaryAction.label}
    </Button>
  );

  if (!isMobile) {
    return (
      <DesktopBar>
        {search}
        {filters}
        <div className="spacer" />
        {secondaryActions.map((s) => (
          <Button
            key={s.key}
            icon={s.icon}
            danger={s.danger}
            onClick={s.onClick}
            style={{ height: 40 }}
          >
            {s.label}
          </Button>
        ))}
        {renderPrimary()}
      </DesktopBar>
    );
  }

  const secondaryMenuItems = secondaryActions.map((s) => ({
    key: s.key,
    icon: s.icon,
    label: s.label,
    danger: s.danger,
    onClick: s.onClick
  }));

  return (
    <>
      <MobileBar>
        <div className="top-row">
          <div className="search">{search}</div>
          {filters.length > 0 && (
            <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
              <Tooltip title="Filters">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setOpen(true)}
                  aria-label="Filters"
                />
              </Tooltip>
            </Badge>
          )}
          {secondaryActions.length > 0 && (
            <Dropdown menu={{ items: secondaryMenuItems }} trigger={['click']}>
              <Button icon={<MoreOutlined />} aria-label="More actions" />
            </Dropdown>
          )}
        </div>
        {primaryAction && (
          <div className="primary-row">{renderPrimary({ width: '100%' })}</div>
        )}
      </MobileBar>
      <Drawer
        title="Filters"
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        width={320}
      >
        <DrawerStack>{filters}</DrawerStack>
      </Drawer>
    </>
  );
};

export default ResponsiveFilterBar;
